/* eslint-disable max-len */
import memoize from "memoizee";
import i18n from "i18next";
import {
  translate as handleTranslate,
  tts as handleTTS,
} from "@nualang/nualang-api-and-queries/APIs/Services";
import config from "../config";

import logger from "../logger";

const ranges = [
  "\ud83c[\udf00-\udfff]", // U+1F300 to U+1F3FF
  "\ud83d[\udc00-\ude4f]", // U+1F400 to U+1F64F
  "\ud83d[\ude80-\udeff]",
  "/(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g",
  "/([#0-9]\u20E3)|[\xA9\xAE\u203C\u2047-\u2049\u2122\u2139\u3030\u303D\u3297\u3299][\uFE00-\uFEFF]?|[\u2190-\u21FF][\uFE00-\uFEFF]?|[\u2300-\u23FF][\uFE00-\uFEFF]?|[\u2460-\u24FF][\uFE00-\uFEFF]?|[\u25A0-\u25FF][\uFE00-\uFEFF]?|[\u2600-\u27BF][\uFE00-\uFEFF]?|[\u2900-\u297F][\uFE00-\uFEFF]?|[\u2B00-\u2BF0][\uFE00-\uFEFF]?|(?:\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDEFF])[\uFE00-\uFEFF]?|[\u20E3]|[\u26A0-\u2FFF]|\uD83E[\udd00-\uddff]|[\u00A0-\u269F]/",
];

const regex = new RegExp(ranges.join("|"), "g");
const removeMarkdown = (str = "") =>
  String(str)
    .trim()
    .replace(/!\[(.*)\]\(([^ ]+?)( "(.+)")?\)/gm, "") // Remove markdown images
    .replace(/(?:[*#])|\[(.*?)\]\(.*?\)/gm, "$1"); // Extract text from markdown links

// const removeSymbs = (str = '') => str.trim().replace(/[`~¡!?()_|+\-=?¿;:'",.<>\\{\\}\\[\]\\\\/]/gi, '');
const removeEmojis = (str = "") => str.replace(regex, "");

export const removeSymbols = (str = "") => {
  const removedMarkdown = removeMarkdown(str);
  const removedEmojis = removeEmojis(removedMarkdown);
  // const removedSymbs = removeSymbs(removedEmojis);
  return removedEmojis.trim().toString();
};

const mTranslate = (body) => handleTranslate(JSON.parse(body));
const memoizeTranslate = memoize(mTranslate, { promise: true });

const mTTS = (body) => handleTTS(JSON.parse(body));
const memoizeTTS = memoize(mTTS, { promise: true });

export default function useServices() {
  const translate = async (text, learnLang, forLang) => {
    try {
      if (learnLang === "irish" || forLang === "irish") {
        alert(
          "Translation is not supported for Irish, We hope to add support for it soon.",
        );
        return null;
      }
      const formattedText = removeSymbols(text);
      const sourceLanguageCode = config.languages[learnLang.toLowerCase()].Code;
      const targetLanguageCode = forLang
        ? config.forLanguages[forLang.toLowerCase()].Code
        : i18n.language.substring(0, 2);
      const body = {
        sourceLanguageCode /* required */,
        targetLanguageCode /* required */,
        text: formattedText /* required */,
      };
      const result = await memoizeTranslate(JSON.stringify(body));
      return result.translatedText;
    } catch (error) {
      throw error;
    }
  };

  const tts = async (
    text,
    learnLang,
    voice,
    languageCode,
    voiceSpeed,
    pitch,
    addEndingPause,
  ) => {
    try {
      if (learnLang === "irish") {
        alert(
          "Text to Speech is not supported for Irish, We hope to add support for it soon.",
        );
        return null;
      }
      // remove symbols and underscores before sending to aws polly
      let cleanText;
      const fillInTheBlankSound = localStorage.getItem(
        "nualang-fill-in-the-blank-sound",
      );
      if (fillInTheBlankSound === "pause") {
        cleanText = removeSymbols(String(text));
        if (voiceSpeed === "x-slow") {
          const strength = "strong";
          cleanText = cleanText
            .split(" ")
            .join(` <break strength="${strength}"/> `);
        }
        cleanText = cleanText.replaceAll("_____");
      } else {
        cleanText = removeSymbols(String(text));
        cleanText = cleanText.replace(/&/g, "&amp;");
        if (voiceSpeed === "x-slow") {
          const strength = "strong";
          cleanText = cleanText
            .split(" ")
            .join(` <break strength="${strength}"/> `);
        } else if (addEndingPause && cleanText && cleanText.length < 10) {
          cleanText = cleanText + '<break time="1s"/>';
        }
        cleanText = cleanText.replaceAll("_____", `<break time="1s"/> `);
      }
      let body;
      const langDefaults =
        (learnLang && config.languages[learnLang.toLowerCase()]) || {};
      // Only the config entry knows which service owns this voice — the name is
      // ambiguous across providers. See utils/voices.
      const voiceInfo = config.resolveVoice(voice, { learnLang, languageCode });
      if (voiceInfo?.ambiguous) {
        // The record carries no accent and the name belongs to more than one, so
        // the language's standard accent was assumed.
        logger.warn(
          `Voice "${voice}" has no stored accent and is ambiguous; assuming ${voiceInfo.languageCode}.`,
        );
      } else if (voice && !voiceInfo) {
        // A name the language tree does not own, so the provider below comes
        // from the legacy lists. Only reachable for a name dropped from the
        // config while still stored on old records.
        logger.warn(
          `Voice "${voice}" is absent from the config; guessing its provider from the name lists.`,
        );
      }
      const isAzure = voiceInfo
        ? voiceInfo.provider === "azure"
        : config.azureVoices.includes(voice);
      const provider = isAzure ? "azure-speech-service" : "polly";
      if (isAzure) {
        const azureVoice = voice || langDefaults.AzureVoice;
        body = {
          text: cleanText,
          voice: azureVoice ? `${azureVoice}Neural` : null,
          // An Azure voice exists only for the locale it is defined under, so
          // its own accent wins over whatever the caller asked for.
          lang: voiceInfo?.languageCode || languageCode,
          rate: voiceSpeed || null,
          engine: "neural",
          pitch: pitch || null,
          provider: provider,
        };
      } else {
        const isNeural = voiceInfo
          ? voiceInfo.isNeuralOnly || (voiceInfo.engine === "neural" && !pitch)
          : config.neuralVoices.includes(voice) && !pitch;
        body = {
          text: cleanText,
          voice: voice || langDefaults.Voice,
          lang: voiceInfo?.languageCode || languageCode || langDefaults.Tag,
          rate: voiceSpeed || null,
          engine: isNeural ? "neural" : "standard",
          pitch: pitch || null,
          provider: provider,
        };
      }
      const result = await memoizeTTS(JSON.stringify(body));
      logger.debug(`Return from ${provider} TTS: ${JSON.stringify(result)}`);
      return result;
    } catch (error) {
      throw error;
    }
  };

  return [tts, translate];
}
